import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";

import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/admin/StatusBadge";
import { toast } from "@/hooks/use-toast";
import { Loader2, ShieldAlert, Trash2 } from "lucide-react";

interface SuppressionRow {
  id: string;
  email: string;
  reason: string;
  metadata: any;
  created_at: string;
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });

const reasonTone = (r: string) => {
  if (r.includes("bounce")) return "danger" as const;
  if (r.includes("complaint")) return "danger" as const;
  if (r.includes("unsubscribe")) return "warn" as const;
  return "neutral" as const;
};

const AdminSuppressions = () => {
  const [rows, setRows] = useState<SuppressionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState<SuppressionRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("suppressed_emails")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000);
    if (!error && data) setRows(data as SuppressionRow[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.email.toLowerCase().includes(q));
  }, [rows, search]);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    const email = confirmDelete.email.toLowerCase();
    const { error } = await supabase.from("suppressed_emails").delete().eq("id", confirmDelete.id);
    if (error) {
      setDeleting(false);
      toast({ title: "Failed to remove", description: error.message, variant: "destructive" });
      return;
    }
    // Also clear any "used" unsubscribe token for this address — otherwise the
    // send function's safety fallback will keep suppressing future emails.
    const { error: tokenError } = await supabase
      .from("email_unsubscribe_tokens")
      .update({ used_at: null })
      .eq("email", email);
    setDeleting(false);
    if (tokenError) {
      toast({
        title: "Removed, but token reset failed",
        description: `${email} was removed, but the unsubscribe token couldn't be reset: ${tokenError.message}`,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Removed from suppression list",
        description: `${email} can now receive emails again.`,
      });
    }
    setRows((prev) => prev.filter((r) => r.id !== confirmDelete.id));
    setConfirmDelete(null);
  };

  return (
    <>
      <Helmet>
        <title>Suppression list · Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="mb-6">
        <h1 className="text-3xl font-semibold tracking-tight">Suppression list</h1>
        <p className="text-muted-foreground mt-1">
          These addresses are blocked from receiving any further emails — usually due to bounces,
          complaints or unsubscribes. Remove an entry to allow emails again.
        </p>
      </header>

      <div className="mb-4">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email…"
          className="max-w-md"
        />
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 flex flex-col items-center justify-center text-muted-foreground text-sm">
            <ShieldAlert className="w-8 h-8 mb-2 opacity-60" />
            No suppressed addresses. 🎉
          </div>
        ) : (
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Reason</th>
                <th className="text-left px-4 py-2 font-medium">Added</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-4 py-3 font-medium break-all">{r.email}</td>
                  <td className="px-4 py-3">
                    <StatusBadge label={r.reason} tone={reasonTone(r.reason)} />
                  </td>
                  <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                    {fmtDate(r.created_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete(r)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from suppression list?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{confirmDelete?.email}</strong> will be allowed to receive emails from the
              site again. Only do this if you're sure the original bounce / complaint was a
              mistake — otherwise it may damage your sender reputation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleting}
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AdminSuppressions;
